/**
 * Human-readable rendering of a server area formula.
 *
 * The server sends the formula as DATA - a fixed sum-of-products grammar:
 *
 *   { sum: [ { product: [ {var|const, divide_by?}, ... ] }, ... ] }
 *
 * `marineCalc.js` EVALUATES that structure; this module only DESCRIBES it, for
 * the catalog surfaces where an installer needs to see what they are buying into
 * without being able to edit it.
 *
 * Display code must never throw: a formula that cannot be rendered yields an
 * empty string and the caller hides the block. That is the opposite of the rule
 * in `marineCalc.evaluateAreaFormula`, which MUST throw - a formula that cannot
 * be evaluated must never silently produce a number.
 *
 * ZERO IMPORTS BY DESIGN - unit-tested directly by
 * `scripts/test/areaFormulaDisplay.test.mjs`. Do not add imports.
 */

function termText(term, labels) {
  if (!term || typeof term !== 'object') return null;
  let name;
  if (Object.prototype.hasOwnProperty.call(term, 'const')) {
    const n = Number(term.const);
    if (!Number.isFinite(n)) return null;
    name = String(n);
  } else if (term.var) {
    name = (labels && labels[term.var]) || term.var;
  } else {
    return null;
  }
  // `divide_by` applies to a const as well as a var. `marineCalc.resolveTerm`
  // divides either one, so an earlier version that returned consts before
  // reaching this check DESCRIBED a formula differently from how it EVALUATED
  // — { const: 30, divide_by: 12 } rendered as "30" and computed as 2.5.
  if (term.divide_by === undefined) return name;
  const divisor = Number(term.divide_by);
  if (!Number.isFinite(divisor) || divisor === 0) return null;
  return `(${name} / ${divisor})`;
}

/**
 * "length overall x (hull height / 12) x 2 + length overall x beam"
 *
 * `labels` maps a var name to display text; an unmapped var renders as its raw
 * wire name, which is ugly but honest and never blank.
 */
export function formulaText(formula, labels) {
  if (!formula || !Array.isArray(formula.sum)) return '';
  const groups = [];
  for (const group of formula.sum) {
    if (!group || !Array.isArray(group.product) || group.product.length === 0) return '';
    const parts = [];
    for (const term of group.product) {
      const text = termText(term, labels);
      if (text === null) return '';
      parts.push(text);
    }
    groups.push(parts.join(' x '));
  }
  return groups.join(' + ');
}

/** The variables a formula references, once each, in first-appearance order. */
export function formulaVars(formula) {
  const out = [];
  for (const group of (formula && formula.sum) || []) {
    for (const term of (group && group.product) || []) {
      if (term && term.var && !out.includes(term.var)) out.push(term.var);
    }
  }
  return out;
}

/**
 * The same expression with the estimator's numbers in place of the names:
 * "22 x (30 / 12) x 2".
 *
 * Returns '' when ANY referenced variable has no usable value. A partly
 * substituted expression would show names and numbers side by side and read as
 * though the calculation had run — the caller hides the line instead.
 */
export function formulaSubstituted(formula, vars) {
  const numeric = {};
  for (const name of formulaVars(formula)) {
    const raw = vars ? vars[name] : undefined;
    if (raw === null || raw === undefined || raw === '' || !Number.isFinite(Number(raw))) return '';
    numeric[name] = String(Number(raw));
  }
  return formulaText(formula, numeric);
}

/**
 * True when the formula is a single bare variable — the MEASURED_AREA case,
 * `{ sum: [ { product: [ { var: 'manual_area_sqft' } ] } ] }`.
 *
 * There is no derivation to show: the estimator types a square footage and that
 * IS the area. Rendering "Area (sq ft)" as an explanation of how the area was
 * calculated is noise, so the caller hides the block.
 */
export function isTrivialFormula(formula) {
  const groups = (formula && formula.sum) || [];
  if (groups.length !== 1) return false;
  const product = (groups[0] && groups[0].product) || [];
  if (product.length !== 1) return false;
  const term = product[0];
  return Boolean(term && term.var && term.divide_by === undefined);
}
